import type { Language } from "@/lib/i18n/translations";

type L<T = string> = Record<Language, T>;

export interface SchemaGenerator {
  slug: string;
  type: string;
  googleDoc: string;
  required: string[];
  recommended: string[];
  name: L;
  metaTitle: L;
  metaDescription: L;
  intro: L;
  useCases: L<string[]>;
  note?: L;
  example: Record<string, unknown>;
}

export const SCHEMA_GENERATORS: SchemaGenerator[] = [
  {
    slug: "product",
    type: "Product",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/product",
    required: ["name", "offers | review | aggregateRating"],
    recommended: ["image", "description", "sku", "brand", "offers.price", "offers.priceCurrency", "offers.availability"],
    name: { en: "Product schema generator", fr: "Générateur de schéma Product", es: "Generador de schema Product" },
    metaTitle: {
      en: "Product Schema Generator (JSON-LD) – Free | SchemasGen",
      fr: "Générateur de schéma Product JSON-LD gratuit | SchemasGen",
      es: "Generador de schema Product JSON-LD gratis | SchemasGen",
    },
    metaDescription: {
      en: "Generate Product JSON-LD from any product page URL: price, availability, brand and SKU taken from the page itself. Free, no signup.",
      fr: "Générez le JSON-LD Product à partir de l'URL d'une fiche produit : prix, disponibilité, marque et SKU tirés de la page. Gratuit, sans inscription.",
      es: "Genera el JSON-LD Product desde la URL de una ficha de producto: precio, disponibilidad, marca y SKU tomados de la página. Gratis, sin registro.",
    },
    intro: {
      en: "Product markup tells Google the name, price and stock status of an item so it can show them directly in search results and Google Shopping. Paste a product page URL and SchemasGen builds the JSON-LD from what is actually on the page.",
      fr: "Le balisage Product indique à Google le nom, le prix et la disponibilité d'un article pour qu'il puisse les afficher directement dans les résultats et Google Shopping. Collez l'URL d'une fiche produit : SchemasGen construit le JSON-LD à partir de ce qui figure réellement sur la page.",
      es: "El marcado Product indica a Google el nombre, el precio y la disponibilidad de un artículo para mostrarlos directamente en los resultados y en Google Shopping. Pega la URL de una ficha de producto y SchemasGen crea el JSON-LD con lo que realmente aparece en la página.",
    },
    useCases: {
      en: ["E-commerce product pages", "Software or SaaS plans with a price", "Product review pages"],
      fr: ["Fiches produits e-commerce", "Offres logicielles ou SaaS avec un prix", "Pages de test de produit"],
      es: ["Fichas de producto e-commerce", "Planes de software o SaaS con precio", "Páginas de reseña de producto"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Example product name",
      image: "https://example.com/product.jpg",
      description: "Short description copied from the page.",
      sku: "SKU-0000",
      brand: { "@type": "Brand", name: "Example brand" },
      offers: {
        "@type": "Offer",
        url: "https://example.com/product",
        priceCurrency: "EUR",
        price: "0.00",
        availability: "https://schema.org/InStock",
      },
    },
  },
  {
    slug: "local-business",
    type: "LocalBusiness",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/local-business",
    required: ["name", "address"],
    recommended: ["telephone", "openingHoursSpecification", "geo", "url", "image", "priceRange"],
    name: { en: "LocalBusiness schema generator", fr: "Générateur de schéma LocalBusiness", es: "Generador de schema LocalBusiness" },
    metaTitle: {
      en: "LocalBusiness Schema Generator (JSON-LD) – Free | SchemasGen",
      fr: "Générateur de schéma LocalBusiness JSON-LD gratuit | SchemasGen",
      es: "Generador de schema LocalBusiness JSON-LD gratis | SchemasGen",
    },
    metaDescription: {
      en: "Create LocalBusiness JSON-LD with your real address, phone and opening hours, extracted from your website. Free test, no signup.",
      fr: "Créez le JSON-LD LocalBusiness avec votre vraie adresse, téléphone et horaires, extraits de votre site. Essai gratuit, sans inscription.",
      es: "Crea el JSON-LD LocalBusiness con tu dirección, teléfono y horario reales, extraídos de tu web. Prueba gratis, sin registro.",
    },
    intro: {
      en: "LocalBusiness markup describes a physical business: address, phone, opening hours. It helps search engines and AI assistants answer \"near me\" questions accurately. SchemasGen only uses details found on your page and leaves out anything it cannot verify.",
      fr: "Le balisage LocalBusiness décrit un commerce physique : adresse, téléphone, horaires. Il aide les moteurs et les assistants IA à répondre correctement aux recherches « près de chez moi ». SchemasGen n'utilise que les informations trouvées sur votre page et laisse de côté ce qu'il ne peut pas vérifier.",
      es: "El marcado LocalBusiness describe un negocio físico: dirección, teléfono, horario. Ayuda a los buscadores y asistentes de IA a responder bien a las búsquedas «cerca de mí». SchemasGen solo usa los datos presentes en tu página y omite lo que no puede verificar.",
    },
    useCases: {
      en: ["Shops, restaurants and agencies", "Multi-location branch pages", "Contact pages"],
      fr: ["Commerces, restaurants et agences", "Pages de succursales", "Pages de contact"],
      es: ["Tiendas, restaurantes y agencias", "Páginas de sucursales", "Páginas de contacto"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Example business",
      url: "https://example.com",
      telephone: "+00 0 00 00 00 00",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Street from the page",
        addressLocality: "City",
        postalCode: "00000",
        addressCountry: "FR",
      },
      openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Friday"], opens: "09:00", closes: "18:00" },
      ],
    },
  },
  {
    slug: "organization",
    type: "Organization",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/organization",
    required: [],
    recommended: ["name", "url", "logo", "sameAs", "contactPoint", "address"],
    name: { en: "Organization schema generator", fr: "Générateur de schéma Organization", es: "Generador de schema Organization" },
    metaTitle: {
      en: "Organization Schema Generator (JSON-LD) – Free | SchemasGen",
      fr: "Générateur de schéma Organization JSON-LD gratuit | SchemasGen",
      es: "Generador de schema Organization JSON-LD gratis | SchemasGen",
    },
    metaDescription: {
      en: "Generate Organization JSON-LD for your homepage: name, logo, social profiles and contact details found on your site. Free, no signup.",
      fr: "Générez le JSON-LD Organization de votre page d'accueil : nom, logo, réseaux sociaux et contacts trouvés sur votre site. Gratuit, sans inscription.",
      es: "Genera el JSON-LD Organization de tu página de inicio: nombre, logo, redes sociales y contacto encontrados en tu web. Gratis, sin registro.",
    },
    intro: {
      en: "Organization markup identifies who is behind a website. It helps Google pick the right logo and knowledge panel details, and helps AI assistants connect your brand to its official profiles. SchemasGen always proposes it when your homepage is missing it.",
      fr: "Le balisage Organization identifie qui est derrière un site. Il aide Google à choisir le bon logo et les bonnes informations, et aide les assistants IA à relier votre marque à ses profils officiels. SchemasGen le propose systématiquement quand votre page d'accueil n'en a pas.",
      es: "El marcado Organization identifica quién está detrás de un sitio. Ayuda a Google a elegir el logo y los datos correctos, y a los asistentes de IA a vincular tu marca con sus perfiles oficiales. SchemasGen lo propone siempre que tu página de inicio no lo tenga.",
    },
    useCases: {
      en: ["Homepages", "About pages", "Company brand sites"],
      fr: ["Pages d'accueil", "Pages « À propos »", "Sites de marque"],
      es: ["Páginas de inicio", "Páginas «Quiénes somos»", "Sitios de marca"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Example company",
      url: "https://example.com",
      logo: "https://example.com/logo-from-the-page.png",
      sameAs: ["https://www.linkedin.com/company/example"],
    },
  },
  {
    slug: "article",
    type: "Article",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/article",
    required: [],
    recommended: ["headline", "image", "datePublished", "dateModified", "author", "author.url"],
    name: { en: "Article schema generator", fr: "Générateur de schéma Article", es: "Generador de schema Article" },
    metaTitle: {
      en: "Article Schema Generator (JSON-LD) for Blogs | SchemasGen",
      fr: "Générateur de schéma Article JSON-LD pour blogs | SchemasGen",
      es: "Generador de schema Article JSON-LD para blogs | SchemasGen",
    },
    metaDescription: {
      en: "Generate Article or BlogPosting JSON-LD from a post URL: headline, author, dates and image read from the page. Free, no signup.",
      fr: "Générez le JSON-LD Article ou BlogPosting depuis l'URL d'un article : titre, auteur, dates et image lus sur la page. Gratuit, sans inscription.",
      es: "Genera el JSON-LD Article o BlogPosting desde la URL de un artículo: título, autor, fechas e imagen leídos en la página. Gratis, sin registro.",
    },
    intro: {
      en: "Article markup tells search engines who wrote a piece and when it was published or updated. Clear authorship and dates are signals Google and AI assistants rely on when choosing sources. SchemasGen picks Article, NewsArticle or BlogPosting depending on the page.",
      fr: "Le balisage Article indique aux moteurs qui a écrit un contenu et quand il a été publié ou mis à jour. Un auteur et des dates clairs sont des signaux utilisés par Google et les assistants IA pour choisir leurs sources. SchemasGen choisit Article, NewsArticle ou BlogPosting selon la page.",
      es: "El marcado Article indica a los buscadores quién escribió un contenido y cuándo se publicó o actualizó. Un autor y fechas claros son señales que Google y los asistentes de IA usan al elegir fuentes. SchemasGen elige Article, NewsArticle o BlogPosting según la página.",
    },
    useCases: {
      en: ["Blog posts", "News articles", "Guides and tutorials"],
      fr: ["Articles de blog", "Actualités", "Guides et tutoriels"],
      es: ["Entradas de blog", "Noticias", "Guías y tutoriales"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: "Title of the post",
      image: ["https://example.com/cover.jpg"],
      datePublished: "YYYY-MM-DD",
      dateModified: "YYYY-MM-DD",
      author: { "@type": "Person", name: "Author name", url: "https://example.com/author" },
    },
  },
  {
    slug: "faq",
    type: "FAQPage",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/faqpage",
    required: ["mainEntity", "Question.name", "Answer.text"],
    recommended: [],
    name: { en: "FAQ schema generator", fr: "Générateur de schéma FAQ", es: "Generador de schema FAQ" },
    metaTitle: {
      en: "FAQ Schema Generator (FAQPage JSON-LD) – Free | SchemasGen",
      fr: "Générateur de schéma FAQ (FAQPage JSON-LD) gratuit | SchemasGen",
      es: "Generador de schema FAQ (FAQPage JSON-LD) gratis | SchemasGen",
    },
    metaDescription: {
      en: "Turn the questions and answers already on your page into valid FAQPage JSON-LD. No invented answers. Free, no signup.",
      fr: "Transformez les questions-réponses déjà présentes sur votre page en JSON-LD FAQPage valide. Aucune réponse inventée. Gratuit, sans inscription.",
      es: "Convierte las preguntas y respuestas que ya están en tu página en JSON-LD FAQPage válido. Sin respuestas inventadas. Gratis, sin registro.",
    },
    intro: {
      en: "FAQPage markup lists the questions and answers visible on a page. SchemasGen only marks up Q&As that really appear on the page, word for word.",
      fr: "Le balisage FAQPage liste les questions et réponses visibles sur une page. SchemasGen ne balise que les questions-réponses réellement présentes sur la page, mot pour mot.",
      es: "El marcado FAQPage enumera las preguntas y respuestas visibles en una página. SchemasGen solo marca las que realmente aparecen en la página, palabra por palabra.",
    },
    note: {
      en: "Since 2023, Google only shows FAQ rich results for well-known government and health websites. The markup is still valid and remains useful for other search engines and AI assistants.",
      fr: "Depuis 2023, Google n'affiche les résultats enrichis FAQ que pour les sites gouvernementaux et de santé reconnus. Le balisage reste valide et utile pour les autres moteurs et les assistants IA.",
      es: "Desde 2023, Google solo muestra resultados enriquecidos de FAQ para sitios gubernamentales y de salud reconocidos. El marcado sigue siendo válido y útil para otros buscadores y asistentes de IA.",
    },
    useCases: {
      en: ["FAQ pages", "Product pages with a Q&A section", "Support pages"],
      fr: ["Pages FAQ", "Fiches produits avec questions-réponses", "Pages d'aide"],
      es: ["Páginas de FAQ", "Fichas de producto con preguntas", "Páginas de ayuda"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Question exactly as shown on the page?",
          acceptedAnswer: { "@type": "Answer", text: "Answer exactly as shown on the page." },
        },
      ],
    },
  },
  {
    slug: "event",
    type: "Event",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/event",
    required: ["name", "startDate", "location"],
    recommended: ["endDate", "eventStatus", "eventAttendanceMode", "image", "description", "offers", "organizer"],
    name: { en: "Event schema generator", fr: "Générateur de schéma Event", es: "Generador de schema Event" },
    metaTitle: {
      en: "Event Schema Generator (JSON-LD) – Free | SchemasGen",
      fr: "Générateur de schéma Event JSON-LD gratuit | SchemasGen",
      es: "Generador de schema Event JSON-LD gratis | SchemasGen",
    },
    metaDescription: {
      en: "Generate Event JSON-LD from an event page: dates, venue, tickets and status taken from the page. Free, no signup.",
      fr: "Générez le JSON-LD Event depuis une page d'événement : dates, lieu, billets et statut tirés de la page. Gratuit, sans inscription.",
      es: "Genera el JSON-LD Event desde la página de un evento: fechas, lugar, entradas y estado tomados de la página. Gratis, sin registro.",
    },
    intro: {
      en: "Event markup lets Google list your concerts, workshops or webinars in its event results with date and location. SchemasGen reads dates and venue from the page and flags missing required fields instead of guessing them.",
      fr: "Le balisage Event permet à Google d'afficher vos concerts, ateliers ou webinaires dans ses résultats d'événements, avec date et lieu. SchemasGen lit les dates et le lieu sur la page et signale les champs obligatoires manquants au lieu de les deviner.",
      es: "El marcado Event permite a Google mostrar tus conciertos, talleres o webinars en sus resultados de eventos, con fecha y lugar. SchemasGen lee las fechas y el lugar en la página y señala los campos obligatorios que faltan en lugar de inventarlos.",
    },
    useCases: {
      en: ["Concerts and shows", "Workshops and trainings", "Online webinars"],
      fr: ["Concerts et spectacles", "Ateliers et formations", "Webinaires en ligne"],
      es: ["Conciertos y espectáculos", "Talleres y formaciones", "Webinars en línea"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "Event",
      name: "Event name",
      startDate: "YYYY-MM-DDTHH:MM",
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: "Venue name",
        address: { "@type": "PostalAddress", addressLocality: "City", addressCountry: "FR" },
      },
    },
  },
  {
    slug: "recipe",
    type: "Recipe",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/recipe",
    required: ["name", "image"],
    recommended: ["recipeIngredient", "recipeInstructions", "totalTime", "recipeYield", "author", "nutrition.calories"],
    name: { en: "Recipe schema generator", fr: "Générateur de schéma Recipe", es: "Generador de schema Recipe" },
    metaTitle: {
      en: "Recipe Schema Generator (JSON-LD) – Free | SchemasGen",
      fr: "Générateur de schéma Recipe (recette) JSON-LD | SchemasGen",
      es: "Generador de schema Recipe (receta) JSON-LD | SchemasGen",
    },
    metaDescription: {
      en: "Generate Recipe JSON-LD from a recipe page: ingredients, steps, cooking time and yield read from the page. Free, no signup.",
      fr: "Générez le JSON-LD Recipe depuis une page de recette : ingrédients, étapes, temps et portions lus sur la page. Gratuit, sans inscription.",
      es: "Genera el JSON-LD Recipe desde una página de receta: ingredientes, pasos, tiempo y raciones leídos en la página. Gratis, sin registro.",
    },
    intro: {
      en: "Recipe markup can show your photo, cooking time and ingredients directly in Google. SchemasGen extracts ingredients and steps from the page and never adds ratings that are not displayed on it.",
      fr: "Le balisage Recipe peut afficher votre photo, le temps de préparation et les ingrédients directement dans Google. SchemasGen extrait ingrédients et étapes de la page et n'ajoute jamais de note qui n'y figure pas.",
      es: "El marcado Recipe puede mostrar tu foto, el tiempo de preparación y los ingredientes directamente en Google. SchemasGen extrae ingredientes y pasos de la página y nunca añade valoraciones que no aparezcan en ella.",
    },
    useCases: {
      en: ["Food blogs", "Recipe sites", "Brand recipe pages"],
      fr: ["Blogs culinaires", "Sites de recettes", "Recettes de marques"],
      es: ["Blogs de cocina", "Sitios de recetas", "Recetas de marcas"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "Recipe",
      name: "Recipe name",
      image: ["https://example.com/dish.jpg"],
      totalTime: "PT45M",
      recipeYield: "4",
      recipeIngredient: ["Ingredient from the page"],
      recipeInstructions: [{ "@type": "HowToStep", text: "Step from the page." }],
    },
  },
  {
    slug: "breadcrumb",
    type: "BreadcrumbList",
    googleDoc: "https://developers.google.com/search/docs/appearance/structured-data/breadcrumb",
    required: ["itemListElement", "ListItem.position", "ListItem.name", "ListItem.item"],
    recommended: [],
    name: { en: "Breadcrumb schema generator", fr: "Générateur de schéma Breadcrumb", es: "Generador de schema Breadcrumb" },
    metaTitle: {
      en: "Breadcrumb Schema Generator (BreadcrumbList JSON-LD) | SchemasGen",
      fr: "Générateur de fil d'Ariane (BreadcrumbList JSON-LD) | SchemasGen",
      es: "Generador de migas de pan (BreadcrumbList JSON-LD) | SchemasGen",
    },
    metaDescription: {
      en: "Generate BreadcrumbList JSON-LD from your page's real navigation path. Cleaner URLs in Google results. Free, no signup.",
      fr: "Générez le JSON-LD BreadcrumbList à partir du vrai chemin de navigation de votre page. Des URL plus lisibles dans Google. Gratuit, sans inscription.",
      es: "Genera el JSON-LD BreadcrumbList a partir de la ruta de navegación real de tu página. URLs más claras en Google. Gratis, sin registro.",
    },
    intro: {
      en: "BreadcrumbList markup shows where a page sits in your site, and Google can display that path instead of a raw URL. SchemasGen builds it from the breadcrumb visible on the page or from the URL structure.",
      fr: "Le balisage BreadcrumbList indique où se trouve une page dans votre site, et Google peut afficher ce chemin à la place de l'URL brute. SchemasGen le construit à partir du fil d'Ariane visible sur la page ou de la structure de l'URL.",
      es: "El marcado BreadcrumbList indica dónde está una página dentro de tu sitio, y Google puede mostrar esa ruta en lugar de la URL. SchemasGen lo crea a partir de las migas de pan visibles en la página o de la estructura de la URL.",
    },
    useCases: {
      en: ["Category and product pages", "Blog posts", "Any page deeper than the homepage"],
      fr: ["Pages catégories et produits", "Articles de blog", "Toute page plus profonde que l'accueil"],
      es: ["Páginas de categoría y producto", "Entradas de blog", "Cualquier página por debajo del inicio"],
    },
    example: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://example.com/" },
        { "@type": "ListItem", position: 2, name: "Category", item: "https://example.com/category" },
      ],
    },
  },
];

export const getGenerator = (slug?: string) => SCHEMA_GENERATORS.find((g) => g.slug === slug);

export const GENERATORS_HUB = {
  metaTitle: {
    en: "Schema Markup Generators by Type (JSON-LD) | SchemasGen",
    fr: "Générateurs de données structurées par type (JSON-LD) | SchemasGen",
    es: "Generadores de datos estructurados por tipo (JSON-LD) | SchemasGen",
  } as L,
  metaDescription: {
    en: "Free schema markup generators for Product, LocalBusiness, Organization, Article, FAQ, Event, Recipe and Breadcrumb. JSON-LD built from your real page content.",
    fr: "Générateurs gratuits de données structurées pour Product, LocalBusiness, Organization, Article, FAQ, Event, Recipe et Breadcrumb. JSON-LD construit à partir du vrai contenu de votre page.",
    es: "Generadores gratuitos de datos estructurados para Product, LocalBusiness, Organization, Article, FAQ, Event, Recipe y Breadcrumb. JSON-LD creado con el contenido real de tu página.",
  } as L,
  h1: {
    en: "Schema markup generators by type",
    fr: "Générateurs de données structurées par type",
    es: "Generadores de datos estructurados por tipo",
  } as L,
  intro: {
    en: "Pick the schema type you need. Each generator reads your page and returns JSON-LD with only the facts it finds there — or let SchemasGen detect the right types automatically from any URL.",
    fr: "Choisissez le type de schéma dont vous avez besoin. Chaque générateur lit votre page et renvoie un JSON-LD contenant uniquement les faits qu'il y trouve — ou laissez SchemasGen détecter automatiquement les bons types à partir de n'importe quelle URL.",
    es: "Elige el tipo de schema que necesitas. Cada generador lee tu página y devuelve un JSON-LD solo con los datos que encuentra — o deja que SchemasGen detecte automáticamente los tipos adecuados desde cualquier URL.",
  } as L,
};
